import React, { FC, memo, useCallback } from 'react';
import UserSelectDropdown from '../../components/shared/UserSelectDropdown/UserSelectDropdown';
import { UserOption, UserOptionsConfig } from '../../utils/helpers/user/userOptionsBuilder';
import { USER_FORMAT_PRESETS, UserData } from '../../utils/helpers/text/userFormatting';

// Module-level constant: a single stable reference shared across all
// SigneeFilter instances so the memoized UserSelectDropdown can actually
// bail out (its internal useMemo for `options` depends on optionsConfig).
const SIGNEE_OPTIONS_CONFIG: UserOptionsConfig = {
  formatConfig: USER_FORMAT_PRESETS.default,
  includeAllUsers: true,
  allUsersPosition: 'first',
  allUsersLabel: 'All users',
  enableSort: true,
};
const EMPTY_STYLE: React.CSSProperties = {};

interface SigneeFilterProps {
  signeeFilter: UserOption | UserData | null;
  allUsers: any[];
  onSigneeFilterChange: (selectedSignee: UserOption) => void;
  width?: number;
  disabled?: boolean;
  style?: React.CSSProperties;
}

/**
 * SigneeFilter - Wrapper around UserSelectDropdown for signee filtering
 */
const SigneeFilterImpl: FC<SigneeFilterProps> = ({
  signeeFilter,
  allUsers,
  onSigneeFilterChange,
  width = 200,
  disabled = false,
  style
}) => {
  // Stable callback: identity only changes when the parent's
  // onSigneeFilterChange identity changes. Without this the inline arrow
  // would be a new reference on every render, defeating the memoized
  // UserSelectDropdown below.
  const handleChange = useCallback((selected: UserOption | null) => {
    if (selected) {
      onSigneeFilterChange(selected);
    } else {
      // Reset to "All users"
      onSigneeFilterChange({ name: '', label: 'All users' });
    }
  }, [onSigneeFilterChange]);

  const resolvedStyle = style || EMPTY_STYLE;

  return (
    <UserSelectDropdown
      value={signeeFilter}
      users={allUsers}
      onChange={handleChange}
      optionsConfig={SIGNEE_OPTIONS_CONFIG}
      label="Signee"
      placeholder="Type to search users"
      width={width}
      disabled={disabled}
      style={resolvedStyle}
      dataTestid="signee-filter-dropdown"
      name="signeeFilter"
      clearable={true}
      returnUserData={true}
    />
  );
};
SigneeFilterImpl.displayName = 'SigneeFilter';

// Wrapping a *named* const with memo PLUS an explicit shallow compare is
// important: CRA's react-refresh-babel transform silently strips memo()
// wrappers in dev mode when no second argument is passed (because it
// re-registers the component as a Fast Refresh boundary), defeating the
// memoization. Passing a compare function preserves the wrapper.
const shallowEqualProps = (prev: SigneeFilterProps, next: SigneeFilterProps) =>
  prev.signeeFilter === next.signeeFilter &&
  prev.allUsers === next.allUsers &&
  prev.onSigneeFilterChange === next.onSigneeFilterChange &&
  prev.width === next.width &&
  prev.disabled === next.disabled &&
  prev.style === next.style;

const SigneeFilter = memo(SigneeFilterImpl, shallowEqualProps);

export default SigneeFilter;
